/**
 * Command palette — Obsidian-style Cmd+K (Ctrl+K on Windows/Linux, also a
 * floating "Search" button on mobile).
 *
 * One unified surface for:
 *   - Navigate to any page (Dashboard, Members, Projects, ...)
 *   - Jump to any entity by name (project, member, app, expense)
 *   - Run admin actions (Sync ADO, Create project, ...)
 *
 * Fuzzy search: matches by token containment + word-boundary scoring.
 * Renders inline over the current page (sheet on mobile).
 *
 * Usage: mounted once at the layout level. Reads entities via context.
 */
import {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Input,
  Caption1,
  Body2,
} from '@fluentui/react-components';
import {
  Search24Regular,
  Board24Regular,
  People24Regular,
  Briefcase24Regular,
  Apps24Regular,
  Money24Regular,
  Receipt24Regular,
  Settings24Regular,
  PlugConnected24Regular,
  TaskListSquarePerson24Regular,
  Grid24Regular,
  History24Regular,
  Phone24Regular,
  PersonKey24Regular,
  ArrowSync24Regular,
  Add24Regular,
} from '@fluentui/react-icons';
import { useAppContext } from '@/store/appContext';
import { useSharePointList } from '@/hooks/useSharePointList';
import type { Project, Employee, Application } from '@/models/types';

interface CommandItem {
  id: string;
  type: 'nav' | 'entity' | 'action';
  category: string;
  title: string;
  subtitle?: string;
  icon: JSX.Element;
  /** What to do when picked — either a route or a callback. */
  route?: string;
  action?: () => void;
}

const NAV_ITEMS: CommandItem[] = [
  { id: 'nav-dashboard', type: 'nav', category: 'Navigate', title: 'Dashboard', icon: <Board24Regular />, route: '/' },
  { id: 'nav-members', type: 'nav', category: 'Navigate', title: 'Members', icon: <People24Regular />, route: '/members' },
  { id: 'nav-projects', type: 'nav', category: 'Navigate', title: 'Projects', icon: <Briefcase24Regular />, route: '/projects' },
  { id: 'nav-applications', type: 'nav', category: 'Navigate', title: 'Applications', icon: <Apps24Regular />, route: '/applications' },
  { id: 'nav-allocations', type: 'nav', category: 'Navigate', title: 'Allocations', icon: <TaskListSquarePerson24Regular />, route: '/allocations' },
  { id: 'nav-access', type: 'nav', category: 'Navigate', title: 'Access Matrix', icon: <Grid24Regular />, route: '/access' },
  { id: 'nav-user-access', type: 'nav', category: 'Navigate', title: 'User Access', icon: <PersonKey24Regular />, route: '/user-access' },
  { id: 'nav-costs', type: 'nav', category: 'Navigate', title: 'Cost Center', icon: <Money24Regular />, route: '/costs' },
  { id: 'nav-cost-allocation', type: 'nav', category: 'Navigate', title: 'Cost Allocation', icon: <Money24Regular />, route: '/cost-allocation' },
  { id: 'nav-expenses', type: 'nav', category: 'Navigate', title: 'Expenses', icon: <Receipt24Regular />, route: '/expenses' },
  { id: 'nav-integrations', type: 'nav', category: 'Navigate', title: 'Integrations', icon: <PlugConnected24Regular />, route: '/integrations' },
  { id: 'nav-audit', type: 'nav', category: 'Navigate', title: 'Audit Log', icon: <History24Regular />, route: '/audit' },
  { id: 'nav-devices', type: 'nav', category: 'Navigate', title: 'Devices', icon: <Phone24Regular />, route: '/devices' },
  { id: 'nav-settings', type: 'nav', category: 'Navigate', title: 'Settings', icon: <Settings24Regular />, route: '/settings' },
];

const ACTION_ITEMS: CommandItem[] = [
  {
    id: 'act-sync-microsoft',
    type: 'action',
    category: 'Actions',
    title: 'Sync Microsoft licenses',
    subtitle: 'Pull latest M365 SKUs into OC_Applications',
    icon: <ArrowSync24Regular />,
    route: '/applications',
  },
  {
    id: 'act-add-expense',
    type: 'action',
    category: 'Actions',
    title: 'Submit expense',
    icon: <Add24Regular />,
    route: '/expenses',
  },
  {
    id: 'act-add-project',
    type: 'action',
    category: 'Actions',
    title: 'Create project',
    icon: <Add24Regular />,
    route: '/projects',
  },
];

const useStyles = makeStyles({
  // Backdrop covers full screen, slightly dimmed
  backdrop: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(15, 23, 42, 0.45)',
    backdropFilter: 'blur(4px)',
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: '12vh',
    '@media (max-width: 600px)': {
      paddingTop: '8vh',
      alignItems: 'flex-start',
    },
  },
  panel: {
    width: '92%',
    maxWidth: '620px',
    maxHeight: '70vh',
    background: tokens.colorNeutralBackground1,
    borderRadius: '14px',
    boxShadow: '0 30px 80px rgba(15,23,42,0.30), 0 8px 24px rgba(15,23,42,0.18)',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden' as const,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  searchBar: {
    padding: '10px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  list: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '4px 0',
  },
  groupHeader: {
    padding: '12px 14px 6px',
    fontSize: '11px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    background: tokens.colorNeutralBackground1,
    position: 'sticky' as const,
    top: 0,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 14px',
    cursor: 'pointer',
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  rowActive: {
    background: tokens.colorBrandBackground2,
  },
  rowIcon: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground2,
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowMain: { flex: 1, minWidth: 0 },
  rowTitle: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    lineHeight: 1.3,
  },
  rowSubtitle: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  empty: {
    padding: '32px 14px',
    textAlign: 'center' as const,
    color: tokens.colorNeutralForeground3,
  },
  footer: {
    padding: '8px 12px',
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    display: 'flex',
    justifyContent: 'space-between',
    background: tokens.colorNeutralBackground2,
  },
  kbd: {
    padding: '1px 6px',
    background: tokens.colorNeutralBackground3,
    borderRadius: '4px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    fontFamily: 'ui-monospace, SF Mono, monospace',
    fontSize: '10px',
  },
});

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const styles = useStyles();
  const navigate = useNavigate();
  const { listIds } = useAppContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);

  // Pull entity lists for fuzzy search. Only fetch when palette has been
  // opened at least once (lazy).
  const [hasOpened, setHasOpened] = useState(false);
  useEffect(() => {
    if (open) setHasOpened(true);
  }, [open]);

  const { items: projects } = useSharePointList<Project>('OC_Projects', {
    autoFetch: hasOpened && !!listIds,
  });
  const { items: employees } = useSharePointList<Employee>('OC_Employees', {
    autoFetch: hasOpened && !!listIds,
  });
  const { items: apps } = useSharePointList<Application>('OC_Applications', {
    autoFetch: hasOpened && !!listIds,
  });

  // Build the full searchable index
  const allItems = useMemo<CommandItem[]>(() => {
    const entityItems: CommandItem[] = [
      ...projects.map((p) => ({
        id: `proj-${p.id}`,
        type: 'entity' as const,
        category: 'Projects',
        title: p.fields.Title,
        subtitle: p.fields.Code,
        icon: <Briefcase24Regular />,
        route: `/projects?p=${p.id}`,
      })),
      ...employees.slice(0, 200).map((e) => ({
        id: `emp-${e.id}`,
        type: 'entity' as const,
        category: 'Members',
        title: e.fields.Title,
        subtitle: `${e.fields.JobTitle ?? ''}${e.fields.Department ? ' · ' + e.fields.Department : ''}`.trim(),
        icon: <People24Regular />,
        route: `/members?id=${e.id}`,
      })),
      ...apps.map((a) => ({
        id: `app-${a.id}`,
        type: 'entity' as const,
        category: 'Applications',
        title: a.fields.Title,
        subtitle: a.fields.Vendor,
        icon: <Apps24Regular />,
        route: '/applications',
      })),
    ];
    return [...NAV_ITEMS, ...ACTION_ITEMS, ...entityItems];
  }, [projects, employees, apps]);

  const filtered = useMemo(() => fuzzy(query, allItems), [query, allItems]);

  // Group by category for nice rendering
  const grouped = useMemo(() => {
    const groups = new Map<string, CommandItem[]>();
    for (const item of filtered) {
      const arr = groups.get(item.category) ?? [];
      arr.push(item);
      groups.set(item.category, arr);
    }
    return Array.from(groups.entries());
  }, [filtered]);

  // Reset state on open/close
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      // Focus input shortly after render
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Keep activeIdx in range
  useEffect(() => {
    if (activeIdx >= filtered.length) {
      setActiveIdx(Math.max(0, filtered.length - 1));
    }
  }, [activeIdx, filtered.length]);

  const choose = useCallback(
    (item: CommandItem) => {
      if (item.action) item.action();
      else if (item.route) navigate(item.route);
      onClose();
    },
    [navigate, onClose],
  );

  const onKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const sel = filtered[activeIdx];
      if (sel) choose(sel);
    }
  };

  if (!open) return null;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div
        className={styles.panel}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKey}
        tabIndex={-1}
      >
        <div className={styles.searchBar}>
          <Input
            ref={inputRef}
            placeholder="Search projects, members, apps, actions…"
            value={query}
            onChange={(_, d) => {
              setQuery(d.value);
              setActiveIdx(0);
            }}
            contentBefore={<Search24Regular />}
            size="large"
            appearance="filled-lighter"
            style={{ width: '100%' }}
          />
        </div>

        <div className={styles.list}>
          {filtered.length === 0 ? (
            <div className={styles.empty}>
              <Body2>No matches</Body2>
              <Caption1 style={{ display: 'block', marginTop: 4 }}>
                Try a name, project code, or "settings"
              </Caption1>
            </div>
          ) : (
            grouped.map(([cat, list]) => (
              <div key={cat}>
                <div className={styles.groupHeader}>{cat}</div>
                {list.map((item) => {
                  const idx = filtered.indexOf(item);
                  const active = idx === activeIdx;
                  return (
                    <div
                      key={item.id}
                      className={`${styles.row} ${active ? styles.rowActive : ''}`}
                      onClick={() => choose(item)}
                      onMouseEnter={() => setActiveIdx(idx)}
                    >
                      <div className={styles.rowIcon}>{item.icon}</div>
                      <div className={styles.rowMain}>
                        <div className={styles.rowTitle}>{item.title}</div>
                        {item.subtitle && (
                          <div className={styles.rowSubtitle}>
                            {item.subtitle}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className={styles.footer}>
          <span>
            <span className={styles.kbd}>↑↓</span> navigate{' · '}
            <span className={styles.kbd}>↵</span> open
          </span>
          <span>
            <span className={styles.kbd}>Esc</span> close
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Lightweight fuzzy match — token-containment with positional bonuses.
 * Good enough for ~500 items, no need for fuse.js.
 *
 * Score = sum over query tokens of:
 *   100 if exact start of any word
 *    50 if contained
 *    25 if subtitle contains
 *     0 otherwise
 */
function fuzzy<T extends { title: string; subtitle?: string }>(
  query: string,
  items: T[],
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  const tokens = q.split(/\s+/).filter(Boolean);

  const scored = items.map((item) => {
    const titleLower = item.title.toLowerCase();
    const subLower = (item.subtitle ?? '').toLowerCase();
    let score = 0;
    for (const t of tokens) {
      if (titleLower.startsWith(t)) score += 110;
      else if (
        titleLower.split(/\s+/).some((w) => w.startsWith(t)) ||
        titleLower.split('-').some((w) => w.startsWith(t))
      )
        score += 80;
      else if (titleLower.includes(t)) score += 50;
      else if (subLower.includes(t)) score += 25;
      else return null; // miss any token → exclude
    }
    return { item, score };
  });

  return scored
    .filter((s): s is { item: T; score: number } => s !== null)
    .sort((a, b) => b.score - a.score)
    .map((s) => s.item);
}
